import type {
    CheckboxFilterOption,
    DateRangeFilterOption,
    FilterOptionWithValue,
    NumberRangeFilterOption,
    PriceRangeFilterOption,
    RadioFilterOption,
    VerticalFilterOption,
} from '../types/filter-types.ts';
import { isValidDateFilterValue } from './date-filter-utils.ts';

export type AnyFilter = Record<string, unknown>;

/**
 * Transform filter options with applied values for AccordionFilters component
 * This utility handles the complex mapping between filter types and their selected values
 */
export function transformFiltersForAccordion<TFilter extends AnyFilter>(
    filters: VerticalFilterOption<TFilter>[],
    appliedFilters: TFilter,
): FilterOptionWithValue<TFilter>[] {
    return filters.map((filter): FilterOptionWithValue<TFilter> => {
        const appliedValue = appliedFilters[filter.id as keyof TFilter];

        switch (filter.type) {
            case 'checkbox':
                return {
                    ...filter,
                    selectedValues: Array.isArray(appliedValue) ? appliedValue : [],
                } as unknown as CheckboxFilterOption<TFilter>;

            case 'radio':
                return {
                    ...filter,
                    selectedValue: typeof appliedValue === 'string' ? appliedValue : null,
                } as unknown as RadioFilterOption<TFilter>;

            case 'dateRange':
                return {
                    ...filter,
                    selectedRange: isValidDateFilterValue(appliedValue) ? appliedValue : null,
                } as unknown as DateRangeFilterOption<TFilter>;

            case 'numberRange':
                return {
                    ...filter,
                    selectedRange: isValidNumericRange(appliedValue) ? appliedValue : null,
                } as unknown as NumberRangeFilterOption<TFilter>;

            case 'priceRange':
                return {
                    ...filter,
                    selectedRange: isValidNumericRange(appliedValue) ? appliedValue : null,
                } as unknown as PriceRangeFilterOption<TFilter>;

            default:
                return filter as unknown as VerticalFilterOption<TFilter>;
        }
    });
}

/**
 * Type guard to check if a value is a valid numeric range for price/number range filters
 * Validates that the value has the expected structure with optional min/max properties
 */
function isValidNumericRange(value: unknown): value is { min?: number; max?: number } {
    if (value === null || typeof value !== 'object') {
        return false;
    }
    const obj = value as Record<string, unknown>;
    const hasMin = 'min' in obj && obj.min !== undefined;
    const hasMax = 'max' in obj && obj.max !== undefined;
    // must have at least one of min or max
    if (!hasMin && !hasMax) {
        return false;
    }
    // if present, min/max must be finite numbers
    if (hasMin && typeof obj.min !== 'number') {
        return false;
    }
    if (hasMax && typeof obj.max !== 'number') {
        return false;
    }
    return true;
}

/**
 * Check if any filters have active values selected
 * This utility checks each filter type for non-empty values
 */
export function hasActiveFilters<TFilter extends AnyFilter>(
    filters: FilterOptionWithValue<TFilter>[],
): boolean {
    return filters.some((filter) => {
        switch (filter.type) {
            case 'checkbox':
                return filter.selectedValues && filter.selectedValues.length > 0;
            case 'radio':
                return filter.selectedValue !== null && filter.selectedValue !== undefined;
            case 'dateRange':
                return (
                    filter.selectedRange &&
                    (('from' in filter.selectedRange &&
                        (filter.selectedRange.from || filter.selectedRange.to)) ||
                        ('unit' in filter.selectedRange && filter.selectedRange.count))
                );
            case 'priceRange':
            case 'numberRange':
                return (
                    filter.selectedRange &&
                    (filter.selectedRange.min !== undefined || filter.selectedRange.max !== undefined)
                );
            default:
                return false;
        }
    });
}
